import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { RootState } from '../../../reducers';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { enter } from '../../../resources/animations';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  PLAN_NAMES,
  SCHOOL_ORGANIZATION_ID,
  JUKU_ORGANIZATION_ID,
  PlanSubjectId,
  PLANS_SORT,
  DAIGAKU_ORGANIZATION_ID
} from '../../../resources/config';
import { Log } from '../../../utils/log';
import { CreatePlanData, Plan } from '../../../models/plan';
import { Dates } from '../../../utils/dates';
import { CustomValidators } from '../../../utils/custom-validator';
import { createPlan, findPlans, initializeCreatePlanState, initializeFindPlansState } from '../../../actions/plan.actions';
import { debounceTime, filter, shareReplay, take, takeUntil } from 'rxjs/operators';
import { getCreatePlanResult, getPlans } from '../../../selectors/plan.selectors';
import { dispatchInfoMessage } from '../../../actions/core.actions';
import { combineLatest, Observable, Subject } from 'rxjs';
import { CurrentDateTime } from 'src/app/models/current-date-time';
import { getCurrentDateTime as getCurrentDateTimeSelector } from '../../../selectors/current-date-time.selectors';
import { PlanUtil } from 'src/app/utils/plan-util';

export interface AddPlanDialogData {
  schoolId: string;
  school: string;
  organization: string;
  subjectId?: string;
  memberCount: number | null;
}

@Component({
  selector: 'app-add-plan-dialog',
  templateUrl: './add-plan-dialog.component.html',
  styleUrls: ['./add-plan-dialog.component.scss'],
  animations: [enter]
})
export class AddPlanDialogComponent implements OnInit, OnDestroy {
  minDate: Date = new Date();
  planForm: UntypedFormGroup;
  schoolId: string;
  school: string;
  organization: string;
  defaultSubjectId: string;
  plans: Plan[];
  subjects = [];
  errorMessage = '';
  planCreating = false;
  schoolOrganizationId = SCHOOL_ORGANIZATION_ID;
  jukuOrganizationId = JUKU_ORGANIZATION_ID;
  daigakuOrganizationId = DAIGAKU_ORGANIZATION_ID;

  /* 人数の初期値設定と、人数の減少のワーニングメッセージに使用 */
  currentDateTime$: Observable<CurrentDateTime>;
  plans$: Observable<Plan[]>;
  memberCountOnPreviousPlan: number | null;
  isMemberCountWarningShown = false;

  private LOG_SOURCE = this.constructor.name;
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<RootState>,
    private dialogRef: MatDialogRef<AddPlanDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: AddPlanDialogData
  ) {}

  ngOnInit(): void {
    this.schoolId = this.data.schoolId;
    this.school = this.data.school;
    this.organization = this.data.organization;
    this.defaultSubjectId = this.data.subjectId ? this.data.subjectId : null;
    this.memberCountOnPreviousPlan = this.data.memberCount;
    PLANS_SORT.map(id => this.subjects.push({ id, name: PLAN_NAMES[id] }));

    this.setUpCurrentDateTime();
    this.setUpPlans();
    this.findPlans();
    this.setUp();
    this.setUpForms();

    // 人数の変更を監視
    this.planForm
      .get('memberCount')
      ?.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(value => {
        const isValueValid = value !== null && value !== '';
        // 直近のプランが存在する場合、かつ、人数の入力が有効な場合、かつ、プランの人数が減らされた場合に警告を表示
        this.isMemberCountWarningShown =
          this.memberCountOnPreviousPlan !== null && isValueValid && value < this.memberCountOnPreviousPlan ? true : false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(initializeFindPlansState());
  }

  addPlan() {
    Object.keys(this.planForm.controls).forEach(key => {
      this.planForm.controls[key].updateValueAndValidity();
    });

    if (this.planForm.invalid) {
      this.planForm.markAllAsTouched();
      return;
    }
    Log.debug(this.LOG_SOURCE, 'プランを作成します');
    this.disableForms();
    this.errorMessage = '';
    const duplicationSubjectPlan: Plan | undefined = this.plans.find(p => p.subjectId === this.planForm.get('subjectId').value);

    const plan: CreatePlanData = {
      organization: this.organization,
      schoolId: this.schoolId,
      school: this.school,
      subjectId: this.planForm.get('subjectId').value,
      memberCount: Number(this.planForm.get('memberCount').value),
      startAt: Dates.simple4YmdStringFromIso(this.planForm.get('startAt').value),
      endAt: Dates.simple4YmdStringFromIso(this.planForm.get('endAt').value)
    };
    if (duplicationSubjectPlan !== undefined) plan.id = duplicationSubjectPlan.id;

    Log.debug(this.LOG_SOURCE, 'create plan', plan);
    this.store.dispatch(createPlan({ plan }));

    this.store
      .select(getCreatePlanResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        this.store.dispatch(initializeCreatePlanState());
        Log.debug(this.LOG_SOURCE, `create plan result: `, result);
        if (result.success) {
          this.dialogRef.close(true);
          this.store.dispatch(
            dispatchInfoMessage({
              message: `プランを登録しました`
            })
          );
          return;
        }

        Log.warn(this.LOG_SOURCE, `create plan error: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
        this.enableForms();
        this.errorMessage = result.error ? `[${result.error.code}] ${result.error.message}` : 'エラーが発生しました';
      });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  onChangeSubject() {
    const newSubjectId = this.planForm.get('subjectId').value;
    if (newSubjectId === PlanSubjectId.TRIAL) {
      this.planForm.patchValue({ memberCount: 1 });
      this.planForm.patchValue({ endAt: Dates.get10DaysLaterDay(this.planForm.get('startAt').value) });
    } else {
      this.planForm.patchValue({ memberCount: '' });
      this.planForm.patchValue({ startAt: '' });
      this.planForm.patchValue({ endAt: '' });
    }

    // 各科目の直近のプランの人数を取得
    combineLatest([this.currentDateTime$, this.plans$])
      .pipe(take(1))
      .subscribe(([currentDateTime, plans]) => {
        this.plans = plans;

        const planForTheSubject = plans.find(plan => plan.subjectId === newSubjectId);
        if (planForTheSubject) {
          const priorityRecord = PlanUtil.getPriorityRecord(planForTheSubject.records, currentDateTime);
          this.memberCountOnPreviousPlan = priorityRecord.record.memberCount;
          this.planForm.patchValue({ memberCount: this.memberCountOnPreviousPlan });
        }
      });
  }

  onChangeStartAt() {
    if (this.planForm.get('subjectId').value === PlanSubjectId.TRIAL) {
      this.planForm.patchValue({ endAt: Dates.get10DaysLaterDay(this.planForm.get('startAt').value) });
    } else {
      if (this.planForm.get('endAt').value === '') {
        this.planForm.patchValue({ endAt: Dates.get1YearsLaterDay(this.planForm.get('startAt').value) });
      } else {
        this.planForm.get('endAt').updateValueAndValidity();
      }
    }
  }

  private setUpCurrentDateTime() {
    this.currentDateTime$ = this.store.select(getCurrentDateTimeSelector).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpPlans() {
    this.plans$ = this.store.select(getPlans).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private findPlans() {
    this.store.dispatch(findPlans({ organization: this.organization, schoolId: this.schoolId }));
  }

  private setUp() {
    this.plans$.pipe(take(1)).subscribe(plans => {
      this.plans = plans;
    });
  }

  private setUpForms() {
    this.planForm = new UntypedFormGroup({
      subjectId: new UntypedFormControl('', [Validators.required]),
      memberCount: new UntypedFormControl('', [Validators.required, Validators.pattern(/^-?(0|[1-9]\d*)?$/)]),
      startAt: new UntypedFormControl('', [Validators.required]),
      endAt: new UntypedFormControl('', [Validators.required, CustomValidators.endAtIsAfterStartAt()])
    });

    this.planForm.get('subjectId').setValue(this.defaultSubjectId);
    if (this.memberCountOnPreviousPlan !== null) {
      this.planForm.get('memberCount').setValue(this.memberCountOnPreviousPlan);
    }

    this.enableForms();
  }

  private disableForms() {
    Object.keys(this.planForm.controls).forEach(ctrlName => {
      this.planForm.get(ctrlName).disable();
    });
    this.planCreating = true;
  }

  private enableForms() {
    Object.keys(this.planForm.controls).forEach(ctrlName => {
      this.planForm.get(ctrlName).enable();
    });
    this.planCreating = false;
  }
}
